import * as Sentry from '@sentry/react';

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, redirect, RouterProvider } from 'react-router-dom';

import { LeadsTableTab, OrdersTableTab, PREORDERS_TABLE_TAB } from '@ekhodealer/ekho-common';

import { DiscountTab } from '@config/paths/discountTabs';
import { paths } from '@config/paths/index';
import { SettingsSection } from '@config/paths/settingsSections';
import { POST_SETUP_REDIRECT_KEY } from '@config/storageKeys';
import RouteErrorBoundary from '@pages/errors/RouteErrorBoundary';
import InviteAuthGuard from '@pages/guards/InviteAuthGuard';
import PortalAccessGuard from '@pages/guards/PortalAccessGuard';
import { SessionDataGuard } from '@pages/guards/SessionDataGuard';
import AnalyticsPage from '@pages/routes/analytics';
import LoginPage from '@pages/routes/auth/Login';
import MintlifyLoginPage from '@pages/routes/auth/MintlifyLogin';
import NoAccessPage from '@pages/routes/auth/NoAccess';
import ResetPasswordPage from '@pages/routes/auth/ResetPassword';
import SignoutPage from '@pages/routes/auth/Signout';
import ConversationsPage from '@pages/routes/conversations';
import DiscountsPage from '@pages/routes/discounts';
import InventoryPage from '@pages/routes/inventory';
import LeadsPage from '@pages/routes/leads';
import ServicesProviderOnboarding from '@pages/routes/onboarding';
import OrdersPage from '@pages/routes/orders';
import PlanDetails from '@pages/routes/plan_details';
import PreordersPage from '@pages/routes/preorders';
import {
	OnboardingDealerInfoStep,
	OnboardingLayout,
	OnboardingProcessingStep,
	OnboardingProductsStep,
	OnboardingSubscribeStep,
} from '@pages/routes/product-onboarding';
import ReportsPage from '@pages/routes/reports';
import ReportsOrdersPage from '@pages/routes/reports_orders';
import ReportsPaymentsPage from '@pages/routes/reports_payment_reconciliation';
import SettingsPage from '@pages/routes/settings';
import SettingsSubPage from '@pages/routes/settings/SettingsSubPage';
import {
	SetupLayout,
	SetupMfaPage,
	SetupProfilePage,
	SetupVerifyEmailPage,
} from '@pages/routes/setup';
import UsagePage from '@pages/routes/usage';

import { AppLayout } from './components/AppLayout';
import { AuthSessionWrapper } from './components/AuthSessionWrapper';
import { DefaultRedirect } from './components/DefaultRedirect';
import { getRecentServicesProviderId } from './utils/getFirstServicesProviderId';

// Lazy-loaded public auth pages
const ForgotPasswordPage = lazy(() => import('@pages/routes/auth/ForgotPassword'));
const UserManagementPage = lazy(() => import('@pages/routes/auth/UserManagement'));

// Lazy-loaded debug pages (dev-only)
const AuthDebugPage = lazy(() => import('@pages/routes/devtools/AuthDebug'));

/**
 * AuthSessionLayout
 * Root layout that provides AuthSessionProvider to all routes.
 * This establishes the Firebase auth listener and populates auth atoms.
 */
const AuthSessionLayout = () => (
	<AuthSessionWrapper>
		<Outlet />
	</AuthSessionWrapper>
);

// DOCUMENT TITLES: To set the browser tab title for a route, add a `handle` property with a `title` field:
// Example: handle: { title: 'Orders' } will display "Admin Portal | Orders" in the browser tab
// The DocumentTitleUpdater component in AppLayout automatically handles this.

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
	{
		// Root layout - provides AuthSessionProvider to entire app
		element: <AuthSessionLayout />,
		errorElement: <RouteErrorBoundary />,
		children: [
			// Root redirect
			{
				path: paths.home.path,
				loader: () => {
					// Check localStorage for most recent provider
					const recentProviderId = getRecentServicesProviderId();
					if (recentProviderId) {
						// Check if we should redirect to AI BDC after onboarding setup
						const postSetupRedirect = localStorage.getItem(POST_SETUP_REDIRECT_KEY);
						if (postSetupRedirect === 'ai-bdc') {
							localStorage.removeItem(POST_SETUP_REDIRECT_KEY);
							return redirect(paths.settingsSection.getHref(recentProviderId, SettingsSection.ProductsAiBdc));
						}
						// Fast path: redirect immediately to cached provider
						return redirect(paths.orders.getHref(recentProviderId, OrdersTableTab.ALL));
					}
					// No cached provider - redirect to select-provider
					return redirect('/select-provider');
				},
			},

			// Public auth routes (no guard needed - AuthSessionProvider just tracks state)
			{
				path: paths.login.path,
				element: (
					<Suspense fallback={null}>
						<LoginPage />
					</Suspense>
				),
			},
			{
				path: paths.forgotPassword.path,
				element: (
					<Suspense fallback={null}>
						<ForgotPasswordPage />
					</Suspense>
				),
			},
			{
				path: paths.resetPassword.path,
				element: (
					<Suspense fallback={null}>
						<ResetPasswordPage />
					</Suspense>
				),
			},
			{
				path: paths.noAccess.path,
				element: (
					<Suspense fallback={null}>
						<NoAccessPage />
					</Suspense>
				),
			},
			{
				path: paths.userManagement.path,
				element: (
					<Suspense fallback={null}>
						<UserManagementPage />
					</Suspense>
				),
			},

			// Debug routes (dev-only - component handles production redirect)
			{
				path: paths.debugAuth.path,
				element: (
					<Suspense fallback={null}>
						<AuthDebugPage />
					</Suspense>
				),
			},

			// Provider selection (needs auth + portal access to read services providers from claims)
			{
				path: '/select-provider',
				element: (
					<PortalAccessGuard>
						<DefaultRedirect />
					</PortalAccessGuard>
				),
			},

			// Setup routes (invite-based authentication)
			{
				path: paths.setup.path,
				element: (
					<InviteAuthGuard>
						<SetupLayout />
					</InviteAuthGuard>
				),
				children: [
					{
						index: true,
						element: <Navigate to={paths.setupProfile.relativePath!} replace />,
					},
					{
						path: paths.setupProfile.relativePath,
						element: <SetupProfilePage />,
					},
					{
						path: paths.setupVerifyEmail.relativePath,
						element: <SetupVerifyEmailPage />,
					},
					{
						path: paths.setupMfa.relativePath,
						element: <SetupMfaPage />,
					},
				],
			},

			// Main protected portal routes (requires full portal access)
			{
				element: (
					<PortalAccessGuard>
						<SessionDataGuard>
							<AppLayout />
						</SessionDataGuard>
					</PortalAccessGuard>
				),
				children: [
					// /:servicesProviderId/settings — landing page (card grid)
					{
						path: paths.settings.path,
						element: <SettingsPage />,
						handle: { title: 'Settings' },
					},

					// /:servicesProviderId/settings/* — sub-pages (business details, AI BDC, etc.)
					{
						path: paths.settingsSection.path,
						element: <SettingsSubPage />,
						handle: { title: 'Settings' },
					},

					// Backward-compatible redirects from old /configuration routes
					{
						path: paths.servicesProviderConfiguration.path,
						loader: ({ params }: { params: { servicesProviderId: string } }) =>
							redirect(
								paths.settings.getHref(params.servicesProviderId || 'default'),
							),
					},
					{
						path: '/:servicesProviderId/configuration/team',
						loader: ({ params }: { params: { servicesProviderId: string } }) =>
							redirect(
								`/${params.servicesProviderId || 'default'}/settings/account/team`,
							),
					},
					{
						path: paths.servicesProviderConfigurationTab.path,
						loader: ({
							params,
						}: {
							params: { servicesProviderId: string; tabId: string };
						}) => {
							const spId = params.servicesProviderId || 'default';
							const tabRedirectMap: Record<string, string> = {
								general: 'products/website',
								'business-details': 'account/business-details',
								inventory: 'account/business-details',
								'bank-account': 'account/bank-account',
								access: 'account/access',
							};
							const newSection = tabRedirectMap[params.tabId || ''] || '';
							return redirect(
								newSection
									? `/${spId}/settings/${newSection}`
									: paths.settings.getHref(spId),
							);
						},
					},

					// /:servicesProviderId/plan-details
					{
						path: paths.plan_details.path,
						element: <PlanDetails />,
						handle: { title: 'Plan Details' },
					},

					// /:servicesProviderId/usage
					{
						path: paths.usage.path,
						element: <UsagePage />,
						handle: { title: 'Usage' },
					},

					// /:servicesProviderId/inventory
					{
						path: paths.inventory.path,
						element: <InventoryPage />,
						handle: { title: 'Inventory' },
					},

					// /:servicesProviderId/inventory/:inventoryItemId/:tab - inventory details with drawer
					{
						path: paths.inventoryDetails.path,
						element: <InventoryPage />,
						errorElement: <RouteErrorBoundary />,
						handle: { title: 'Inventory' },
					},

					// Redirect /:servicesProviderId/orders -> /:servicesProviderId/orders/all
					{
						path: paths.orders.path,
						loader: ({ params }: { params: { servicesProviderId?: string } }) =>
							redirect(
								paths.orders.getHref(
									params.servicesProviderId || 'default',
									OrdersTableTab.ALL,
								),
							),
					},

					// /:servicesProviderId/orders/:tabId - single route with tabId param
					{
						path: paths.ordersTab.path,
						element: <OrdersPage />,
						handle: { title: 'Orders' },
					},

					// /:servicesProviderId/orders/:tabId/:orderId/:detailsTab - order details with drawer
					{
						path: paths.orderDetails.path,
						element: <OrdersPage />,
						handle: { title: 'Orders' },
					},

					// Redirect /:servicesProviderId/preorders -> /:servicesProviderId/preorders/all
					{
						path: paths.preorders.path,
						loader: ({ params }: { params: { servicesProviderId?: string } }) =>
							redirect(
								paths.preorders.getHref(
									params.servicesProviderId || 'default',
									PREORDERS_TABLE_TAB.ALL,
								),
							),
					},

					// /:servicesProviderId/preorders/:tabId/:preorderId/:detailsTab - preorder details with drawer
					{
						path: paths.preorderDetails.path,
						element: <PreordersPage />,
						handle: { title: 'Preorders' },
					},

					// /:servicesProviderId/preorders/:tabId
					{
						path: paths.preordersTab.path,
						element: <PreordersPage />,
						handle: { title: 'Preorders' },
					},

					// Redirect /:servicesProviderId/discounts -> /:servicesProviderId/discounts/all
					{
						path: paths.discounts.path,
						loader: ({ params }: { params: { servicesProviderId?: string } }) =>
							redirect(
								paths.discounts.getHref(
									params.servicesProviderId ?? 'default',
									DiscountTab.ALL,
								),
							),
					},

					// /:servicesProviderId/discounts/:tabId/:discountId/:detailsTab - discount details with drawer
					{
						path: paths.discountDetails.path,
						element: <DiscountsPage />,
						handle: { title: 'Discounts' },
					},

					// /:servicesProviderId/discounts/:tabId - single route with tabId param
					{
						path: paths.discountsTab.path,
						element: <DiscountsPage />,
						handle: { title: 'Discounts' },
					},

					// /:servicesProviderId/analytics
					{
						path: paths.analytics.path,
						element: <AnalyticsPage />,
						handle: { title: 'Analytics' },
					},

					// /:servicesProviderId/conversations
					{
						path: paths.conversations.path,
						element: <ConversationsPage />,
						handle: { title: 'Inbox' },
					},

					// /:servicesProviderId/conversations/:conversationId
					{
						path: paths.conversation.path,
						element: <ConversationsPage />,
						handle: { title: 'Inbox' },
					},

					// Redirect /:servicesProviderId/leads -> /:servicesProviderId/leads/all/table
					{
						path: paths.leads.path,
						loader: ({ params }: { params: { servicesProviderId?: string } }) =>
							redirect(
								`/${params.servicesProviderId ?? 'default'}/leads/${
									LeadsTableTab.ALL
								}/table`,
							),
					},

					// /:servicesProviderId/leads/:tabId/:leadId/:detailsTab - lead details with drawer
					{
						path: paths.leadDetails.path,
						element: <LeadsPage />,
						handle: { title: 'Leads' },
					},

					// /:servicesProviderId/leads/:tabId/:viewType? - single route with tabId and optional viewType param
					{
						path: '/:servicesProviderId/leads/:tabId/:viewType?',
						element: <LeadsPage />,
						errorElement: <RouteErrorBoundary />,
						handle: { title: 'Leads' },
					},

					// /:servicesProviderId/reports
					{
						path: paths.reports.path,
						element: <ReportsPage />,
						handle: { title: 'Reports' },
					},

					// /:servicesProviderId/reports/orders
					{
						path: paths.reportsOrders.path,
						element: <ReportsOrdersPage />,
						handle: { title: 'Reports - Orders' },
					},

					// /:servicesProviderId/reports/payments
					{
						path: paths.reportsPayments.path,
						element: <ReportsPaymentsPage />,
						handle: { title: 'Reports - Payment Reconciliation' },
					},
				],
			},

			// New dealer onboarding — public self-serve flow
			// Dealer Info → Products → Payment → Processing → redirect to invite URL
			{
				path: paths.onboarding.path,
				element: <OnboardingLayout />,
				handle: { title: 'Onboarding' },
				children: [
					{
						index: true,
						element: <OnboardingDealerInfoStep />,
					},
					{
						path: paths.onboardingProducts.relativePath,
						element: <OnboardingProductsStep />,
					},
					{
						path: paths.onboardingSubscribe.relativePath,
						element: <OnboardingSubscribeStep />,
					},
					{
						path: paths.onboardingProcessing.relativePath,
						element: <OnboardingProcessingStep />,
					},
				],
			},

			// Existing dealer subscribe flow — /:svpId/subscribe
			// Subscribe (index) → Processing; Products reachable via "Previous"
			{
				path: paths.svpSubscribe.path,
				element: <OnboardingLayout />,
				handle: { title: 'Subscribe' },
				children: [
					{
						index: true,
						element: <OnboardingSubscribeStep />,
					},
					{
						path: paths.svpSubscribeProducts.relativePath,
						element: <OnboardingProductsStep />,
					},
					{
						path: paths.svpSubscribeProcessing.relativePath,
						element: <OnboardingProcessingStep />,
					},
				],
			},

			// Dealer onboarding (invite-based authentication)
			{
				path: paths.dealerOnboarding.path,
				element: (
					<InviteAuthGuard>
						<ServicesProviderOnboarding />
					</InviteAuthGuard>
				),
				handle: { title: 'Onboarding' },
			},

			// Signout (no guard - anyone can attempt to sign out)
			{
				path: paths.signout.path,
				element: <SignoutPage />,
			},

			// Catch-all redirect to home
			{
				path: '*',
				loader: () => redirect(paths.home.getHref()),
			},
		],
	},

	// 4) Mintlify login route (unguarded - handles its own auth)
	{
		path: paths.mintlifyLogin.path,
		element: <MintlifyLoginPage />,
		errorElement: <RouteErrorBoundary />,
	},
]);

export default function AppRouter() {
	return <RouterProvider router={router} />;
}
